import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Layout, Row, Col, theme, App, Alert, Button, Flex, Spin, Tag, Typography, ConfigProvider, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import Header from '../../widgets/header/Header';
import Calculator from '../../widgets/calculator/Calculator';
import type { CalculatorData, ProductDataStatus } from '../../widgets/calculator/Calculator';
import CalculatorSummary from '../../widgets/calculator/CalculatorSummary';
import BsrChart from '../../widgets/forecast/BsrChart';
import SalesPlan from '../../widgets/forecast/SalesPlan';
import type { SalesPlanData } from '../../widgets/forecast/SalesPlan';
import Budget from '../../widgets/budget/Budget';
import type { BudgetCategory } from '../../widgets/budget/Budget';
import ForecastAmazonUS from '../../widgets/forecast/ForecastAmazonUS';
import type { ForecastSummaryData } from '../../widgets/forecast/ForecastAmazonUS';
import Summary from '../../widgets/summary/Summary';
import Notes from '../../widgets/notes/Notes';
import CompetitorAnalysis from '../../widgets/competitors/CompetitorAnalysis';
import ProductHistory from '../../widgets/product-history/ProductHistory';
import UserHistoryBar from '../../widgets/user-history/UserHistoryBar';
import HistoryPage from '../../widgets/history/HistoryPage';
import FoldSection from '../../shared/ui/FoldSection';
import ResultBar from '../../widgets/calculator/ResultBar';
import type { ParentLink } from '../../widgets/parent/ParentSearch';
import {
  FEED_SECTIONS,
  TAB_SECTIONS,
  UNIT_SUMMARY_ID,
  isSectionShown,
  loadViewMode,
  storeViewMode,
  type DashboardViewMode,
} from '../../shared/lib/dashboardView';
import { useThemeMode } from '../../shared/theme';
import { isAsin } from '../../shared/lib/asin';
import CriteriaScore from '../../widgets/scoring/CriteriaScore';
import type { ManualCriteria } from '../../widgets/scoring/scoringInput';
import {
  saveProductCalculation,
  type ParentCandidate,
  loadSavedResult,
  loadResultById,
  loadProductCalculation,
  fetchSharedResult,
  fetchProductCard,
  type ProductCardFields,
  type ProductCalculationDetail,
} from '../../shared/api/client';
import { SHARE_HANDOFF_KEY, clearShareHandoff } from '../../shared/session/shareHandoff';
import { hostLocationWithoutShare, readResultIdFromHostLocation, readShareIdFromHostLocation } from '../../shared/lib/platformShareLink';
import { mapSalesPlan, mapCalcData, productCalculationPayloadToLoadedResult } from '../../shared/lib/resultMapping';
import { sumBudget, sumCategoryItems, computeCogs, computeFullUnitCost, LAUNCH_BUDGET_DEFAULT } from '../../shared/lib/budget';
import { formatMoney } from '../../shared/lib/formatters';
import { buildBackendPayload } from '../../shared/lib/buildBackendPayload';
import type { ProductEntry } from '../../shared/lib/productEntry';

// Блоки страницы расчёта для переходов из липкой полосы — те же якоря, что у шапки.

interface DashboardViewProps {
  // В embed свой Header не рендерится: хром (навигация, пользователь, язык)
  // даёт платформа, а персональные действия шапки — сохранение, шаринг, экспорт —
  // ждут платформенной авторизации из S2.
  withHeader?: boolean;
  // Товар, с которым платформа открыла модуль. Есть только в embed — в
  // standalone своей карточки платформы нет, App её и не передаёт.
  productEntry?: ProductEntry | null;
  /** Номер карточки биржи, с которой платформа открыла модуль (тул на карточке биржи). */
  exchangeXid?: string | null;
}

/** Вкладки страницы в платформе без карточки товара: сам расчёт и история расчётов. */
type PageTab = 'calc' | 'history';

const DashboardView: React.FC<DashboardViewProps> = ({ withHeader = true, productEntry, exchangeXid }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [calculatorData, setCalculatorData] = useState<CalculatorData | undefined>();
  // Ручные критерии оценки живут здесь, а не в калькуляторе: их правят в «Оценке по
  // критериям», а сохраняются они вместе с расчётом — поэтому подмешиваются к его данным.
  const [viewMode, setViewMode] = useState<DashboardViewMode>(loadViewMode);
  const [activeTab, setActiveTab] = useState<string>(UNIT_SUMMARY_ID);
  const [manualCriteria, setManualCriteria] = useState<ManualCriteria>({ fragility: null, differentiation: null });
  const calcForSave = useMemo(
    () => (calculatorData ? { ...calculatorData, ...manualCriteria } : undefined),
    [calculatorData, manualCriteria],
  );
  const [productCard, setProductCard] = useState<ProductCardFields | null>(null);
  const [salesPlanData, setSalesPlanData] = useState<SalesPlanData | undefined>();
  const [budgetData, setBudgetData] = useState<BudgetCategory[] | undefined>();
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryData | null>(null);
  const [asin, setAsin] = useState('');
  const [competitorAsins, setCompetitorAsins] = useState<string[]>([]);
  const [loadedResult, setLoadedResult] = useState<Record<string, unknown> | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [notesData, setNotesData] = useState<string>('');
  // Данные товара по ASIN из калькулятора. Нет их — расчёт помечается, пока пользователь
  // не подтвердит, что считает по введённым вручную числам (по ASIN, к которому относится).
  const [productData, setProductData] = useState<{ status: ProductDataStatus; asin: string }>({ status: 'none', asin: '' });
  const [manualForAsin, setManualForAsin] = useState<string | null>(null);
  const handleProductDataStatus = useCallback((status: ProductDataStatus, forAsin: string) => {
    setProductData({ status, asin: forAsin });
  }, []);
  const productMissing = productData.status === 'missing' && manualForAsin !== productData.asin;
  const resultsUnverified = productMissing || productData.status === 'loading';

  /**
   * Расчёт по шар-ссылке грузится так же, как из истории: данные + имя в поле
   * названия. Имя — из самой записи: получателю оно нужно, чтобы одним «Сохранить»
   * завести свою копию под тем же названием (имена разных владельцев не спорят,
   * миграция 008). Без имени копия была бы «безымянной» до ручного ввода.
   */
  const applySharedResult = useCallback((data: Record<string, unknown>) => {
    setLoadedResult(data);
    setLoadKey((k) => k + 1);
    setLoadedFileName(typeof data.fileName === 'string' ? data.fileName : '');
  }, []);
  const handleLoadProductCalculation = useCallback((detail: ProductCalculationDetail) => {
    setLoadedResult(productCalculationPayloadToLoadedResult(detail.payload));
    setLoadKey((k) => k + 1);
  }, []);
  const reportSharedLoadFailure = useCallback(
    () => message.error(t('idea_calc.messages.load_shared_error')),
    [message, t],
  );

  useEffect(() => {
    // Ключ пишет ТОЛЬКО маршрут `/s/:shareId`, а он существует ровно в том дереве
    // маршрутов, где рендерится наш Header (`App.tsx: createStandaloneMode`). В embed
    // писателя нет вовсе, зато sessionStorage там общий со страницей платформы:
    // читать и стирать в нём ключ значило бы трогать чужое хранилище без единого
    // шанса что-то в нём найти. Второй признак «мы в embed» здесь не заводится
    // (запрет из App.tsx) — берётся тот же проп, которым режимы уже различены.
    if (!withHeader) return;
    const shareId = sessionStorage.getItem(SHARE_HANDOFF_KEY);
    if (shareId) {
      clearShareHandoff();
      fetchSharedResult(shareId).then(applySharedResult).catch(reportSharedLoadFailure);
    }
  }, [withHeader, applySharedResult, reportSharedLoadFailure]);

  // Хост назвал конкретный расчёт — показываем его, а не новый лист по карточке
  // товара. Ключ из примитивов: платформа шлёт новый объект контекста на смену темы.
  const linkedCalculation = productEntry?.calculation ?? null;
  const linkedCalculationKey =
    linkedCalculation === null
      ? null
      : linkedCalculation.kind === 'share'
        ? `share:${linkedCalculation.shareId}`
        : linkedCalculation.kind === 'history'
          ? `history:${linkedCalculation.calculationId}`
          : `invalid:${linkedCalculation.reason}`;
  // Ключ расчёта, который не загрузился. Хранится ключ, а не флаг: сменился расчёт — старый
  // отказ к нему не относится, и сбрасывать флаг отдельным setState в эффекте не нужно.
  const [failedLinkedKey, setFailedLinkedKey] = useState<string | null>(null);
  const linkedFailed =
    linkedCalculation?.kind === 'invalid' || (linkedCalculationKey !== null && failedLinkedKey === linkedCalculationKey);

  useEffect(() => {
    if (!productEntry || linkedCalculation === null) return;
    let cancelled = false;
    if (linkedCalculation.kind === 'invalid') {
      message.error(t('idea_calc.messages.load_calc_error'));
      return;
    }
    const load =
      linkedCalculation.kind === 'share'
        ? fetchSharedResult(linkedCalculation.shareId).then((data) => {
            if (!cancelled) applySharedResult(data);
          })
        : loadProductCalculation(productEntry.productId, linkedCalculation.calculationId).then((detail) => {
            if (!cancelled) handleLoadProductCalculation(detail);
          });
    load.catch(() => {
      if (cancelled) return;
      setFailedLinkedKey(linkedCalculationKey);
      message.error(
        t(linkedCalculation.kind === 'share' ? 'idea_calc.messages.load_shared_error' : 'idea_calc.messages.load_calc_error'),
      );
    });
    return () => {
      cancelled = true;
    };
    // Зависимости — id товара и ключ расчёта, не сами объекты (см. комментарий выше).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEntry?.productId, linkedCalculationKey]);

  useEffect(() => {
    // Названный расчёт открывается как есть: подставить поверх него карточку товара
    // значило бы показать не тот просчёт, который к лаунчу привязан (WS 15556998).
    // Не загрузился — об этом сказано ошибкой, а не подменой листом по карточке.
    if (!productEntry || productEntry.calculation !== null) return;
    let cancelled = false;
    fetchProductCard(productEntry.productId)
      .then((card) => {
        if (!cancelled) setProductCard(card);
      })
      .catch(() => {
        if (!cancelled) message.error(t('idea_calc.messages.load_product_card_error'));
      });
    return () => {
      cancelled = true;
    };
    // Зависимость — id, не сам `productEntry`: платформа шлёт новый объект
    // контекста при смене темы (см. App.tsx), а карточка привязана к товару,
    // не к идентичности объекта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEntry?.productId, linkedCalculationKey]);

  useEffect(() => {
    // Ссылка «Поделиться» из платформы: `/apps/profitCalculator?share=<id>` (или с id
    // в подпути — когда платформа заведёт wildcard). Адрес видим только у хоста, и
    // читается он ровно в том монтировании, куда такая ссылка ведёт: standalone-вью
    // в embed (нет ни карточки товара, ни своего Header). id из адреса убирается
    // сразу, иначе «Обновить страницу» перезатёр бы правки получателя расчётом.
    // Так же читается `?result=<id>` — адрес своего расчёта из таблицы истории, открытого
    // в новой вкладке или по ссылке.
    if (productEntry || withHeader) return;
    const shareId = readShareIdFromHostLocation(window.location);
    const resultId = readResultIdFromHostLocation(window.location);
    if (!shareId && resultId === null) return;
    window.history.replaceState(window.history.state, '', hostLocationWithoutShare(window.location));
    if (shareId) {
      fetchSharedResult(shareId).then(applySharedResult).catch(reportSharedLoadFailure);
    } else if (resultId !== null) {
      loadResultById(resultId)
        .then(applySharedResult)
        .catch(() => message.error(t('idea_calc.messages.load_calc_error')));
    }
  }, [productEntry, withHeader, applySharedResult, reportSharedLoadFailure, message, t]);

  const handleCalcChange = useCallback((data: CalculatorData) => {
    setCalculatorData(data);
    // `asin` питает только виджеты, которые идут с ним в Keepa (конкуренты, график BSR,
    // сезонность). Пока ASIN набирается, им нечего запрашивать: обрывок вроде «B» уходил
    // в /api/keepa-history на каждый символ (QA, WS 15587410, п.4).
    setAsin(data.asin && isAsin(data.asin) ? data.asin : '');
    if (data.competitorAsins?.length) setCompetitorAsins(data.competitorAsins);
  }, []);

  const handleLoadResult = useCallback(async (fileName: string): Promise<boolean> => {
    try {
      const data = await loadSavedResult(fileName + '.json');
      setLoadedResult(data);
      setLoadKey((k) => k + 1);
      setLoadedFileName(fileName);
      return true;
    } catch {
      message.error(t('idea_calc.messages.load_calc_error'));
      return false;
    }
  }, []);

  // Монтирование без карточки товара (`/apps/profitCalculator`): своего Header тут нет,
  // и история — соседняя вкладка, а не шторка поверх расчёта (QA 08.10, WS 15587410, п.1).
  const userHistoryMode = !productEntry && !withHeader;
  const [pageTab, setPageTab] = useState<PageTab>('calc');
  const historyTabShown = userHistoryMode && pageTab === 'history';
  // Выбранный в истории расчёт открывается на вкладке «Расчёт» — но только когда он
  // загрузился: при отказе пользователь остаётся в истории рядом с сообщением об ошибке.
  const openFromHistory = useCallback(
    (fileName: string) => {
      void handleLoadResult(fileName).then((loaded) => {
        if (loaded) setPageTab('calc');
      });
    },
    [handleLoadResult],
  );
  const openSharedFromHistory = useCallback(
    (data: Record<string, unknown>) => {
      applySharedResult(data);
      setPageTab('calc');
    },
    [applySharedResult],
  );

  // Payload карточки товара собирается по клику в ProductHistory, а не на каждое
  // изменение калькулятора — функция, а не мемоизированный объект.
  const buildProductHistoryPayload = useCallback(
    () => buildBackendPayload(calcForSave, salesPlanData, budgetData, notesData),
    [calcForSave, salesPlanData, budgetData, notesData],
  );

  // Привязка к parent (п.5): расчёт целиком — с бюджетом, планом и заметками — уходит в
  // историю карты товара parent, а в самом расчёте остаётся отметка, к кому привязан.
  const handleBindParent = useCallback(
    async (candidate: ParentCandidate): Promise<ParentLink> => {
      if (!candidate.product_id) throw new Error('parent has no product card');
      const link: ParentLink = {
        asin: candidate.asin,
        product_id: candidate.product_id,
        shops: candidate.shops,
        relevance: candidate.relevance,
        bound_at: new Date().toISOString(),
        rating: candidate.rating,
        reviews: candidate.reviews,
        data_at: candidate.data_at,
      };
      const own = calculatorData?.asin || loadedFileName || '';
      await saveProductCalculation(
        candidate.product_id,
        t('idea_calc.parent.bound_title', { asin: own, parent: candidate.asin }),
        { ...buildProductHistoryPayload(), parent_link: link },
      );
      message.success(t('idea_calc.parent.bound_ok', { parent: candidate.asin }));
      return link;
    },
    [calculatorData?.asin, loadedFileName, buildProductHistoryPayload, message, t],
  );

  const calcInitial = useMemo(
    () => loadedResult ? mapCalcData(loadedResult.calculation_data as Record<string, unknown>) : undefined,
    [loadedResult],
  );
  const budgetInitial = useMemo(() => loadedResult?.budget_data as any, [loadedResult]);
  const salesPlanInitial = useMemo(() => mapSalesPlan(loadedResult?.sales_plan), [loadedResult]);
  const notesInitial = useMemo(() => loadedResult?.notes as string | undefined, [loadedResult]);
  // Загруженный расчёт приносит свои ручные критерии; новый — начинается без них.
  useEffect(() => {
    setManualCriteria({ fragility: calcInitial?.fragility ?? null, differentiation: calcInitial?.differentiation ?? null });
  }, [calcInitial]);

  // Calculate total budget from budget categories (matching Budget.tsx logic)
  const totalLaunchBudget = useMemo(() => {
    if (!budgetData) return LAUNCH_BUDGET_DEFAULT;
    const cogs = calculatorData ? computeCogs(calculatorData) : 5;
    const fullUnitCost = calculatorData ? computeFullUnitCost(calculatorData) : 0;
    return sumBudget(budgetData, cogs, fullUnitCost);
  }, [budgetData, calculatorData]);

  // Сводки свёрнутых блоков (раскладка «вариант 4», фидбек 29.09): блок свёрнут,
  // но его итог виден сразу — сумма бюджета, продажи по месяцам.
  const budgetCategories = useMemo(() => {
    if (!budgetData) return [];
    const cogs = calculatorData ? computeCogs(calculatorData) : 5;
    const fullUnitCost = calculatorData ? computeFullUnitCost(calculatorData) : 0;
    return budgetData.map((cat) => ({ id: cat.id, name: cat.name, color: cat.color, total: sumCategoryItems(cat.items, cogs, fullUnitCost) }));
  }, [budgetData, calculatorData]);
  const forecastMonths = useMemo(() => {
    if (!forecastSummary || forecastSummary.sellingPrice <= 0) return [];
    return forecastSummary.monthlyData.slice(0, 6).map((m) => ({ month: m.month, units: Math.round(m.revenue / forecastSummary.sellingPrice) }));
  }, [forecastSummary]);

  // Страница расчёта — «крупно» (вариант 4, фидбек 29.09): тот же цвет дизайн-системы,
  // но без её compact-алгоритма — крупнее шрифт и поля. Только здесь: модалки и шапка
  // остаются в масштабе платформы.
  const themeMode = useThemeMode();
  const pageTheme = useMemo(
    () => ({
      algorithm: themeMode === 'dark' ? [theme.darkAlgorithm] : [theme.defaultAlgorithm],
      // Поля калькулятора и бюджета — маленького размера (size="small"): их высота и
      // шрифт поднимаются здесь, а не правкой каждого из десятков полей.
      // Читаемость текстовых токенов (подписи, сигнальные цвета, плейсхолдер) задаёт
      // общая тема модуля — appTheme.ts; здесь только масштаб.
      token: { fontSize: 15, controlHeight: 38, controlHeightSM: 34 },
      components: {
        InputNumber: { inputFontSizeSM: 15 },
        Input: { inputFontSizeSM: 15 },
      },
    }),
    [themeMode],
  );

  const tabs = viewMode === 'tabs';
  const shown = (id: string) => isSectionShown(viewMode, activeTab, id);
  // Вкладка «Калькулятор» держит «Сводку» рядом, как в ленте: форма во всю ширину
  // экрана растягивала поля в полосы, а результат правки не было видно. Вкладка
  // «Сводка» открывает её одну на всю ширину.
  const summaryShown = shown(UNIT_SUMMARY_ID) || (tabs && activeTab === 'calculator');
  const summaryAlone = tabs && activeTab === UNIT_SUMMARY_ID;
  // Ссылки шапки во вкладках открывают блок, а не прокручивают к скрытому якорю.
  const navigateFromHeader = (id: string) => {
    if (!tabs) return false;
    setActiveTab(id);
    return true;
  };
  const changeViewMode = (mode: DashboardViewMode) => {
    setViewMode(mode);
    storeViewMode(mode);
  };

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      {withHeader && (
        <Header calculatorData={calcForSave} salesPlanData={salesPlanData} budgetData={budgetData} notesData={notesData} onLoadResult={handleLoadResult} onLoadShared={applySharedResult} loadedFileName={loadedFileName} onNavigate={navigateFromHeader} />
      )}
      <Layout.Content
        style={{
          maxWidth: 1720,
          width: '100%',
          margin: '0 auto',
          padding: token.padding,
        }}
      >
        {/* position: relative — точка отсчёта для сайдбара истории: он рендерится
            без портала, чтобы не вылезать за пределы слота модуля на странице
            платформы (см. ProductHistory). */}
        <ConfigProvider theme={pageTheme}>
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {productEntry && (
            <ProductHistory
              productId={productEntry.productId}
              buildPayload={buildProductHistoryPayload}
              onLoad={handleLoadProductCalculation}
            />
          )}
          {userHistoryMode && (
            <>
              <Tabs
                activeKey={pageTab}
                onChange={(key) => setPageTab(key === 'history' ? 'history' : 'calc')}
                items={[
                  { key: 'calc', label: t('idea_calc.user_history.tab_calc') },
                  { key: 'history', label: t('idea_calc.user_history.tab_history') },
                ]}
              />
              <div hidden={!historyTabShown} data-testid="history-page">
                <HistoryPage
                  active={historyTabShown}
                  onLoadResult={openFromHistory}
                  onLoadShared={openSharedFromHistory}
                />
              </div>
            </>
          )}
          {/* Расчёт на вкладке истории скрыт, а не размонтирован: калькулятор, бюджет и план
              берут начальные значения из загруженного расчёта, и перемонтирование стёрло бы
              несохранённые правки. `display`, а не атрибут `hidden`: инлайновый flex его
              перебил бы. */}
          <div
            data-testid="calc-page"
            style={{ display: historyTabShown ? 'none' : 'flex', flexDirection: 'column', gap: 20 }}
          >
          {/* Монтирование без карточки товара (`/apps/profitCalculator`, `entry`
              есть, `productEntry` пуст): своего Header тут нет (`withHeader=false`),
              а без него сохранение было бы недостижимо вовсе. В standalone эти же
              действия уже живут в Header — вторая строка там лишняя. */}
          {userHistoryMode && (
            <UserHistoryBar buildPayload={buildProductHistoryPayload} loadedFileName={loadedFileName} />
          )}
          {/* Пока названный хостом расчёт не пришёл, листа нет: калькулятор без данных рисует
              заводские числа (35.59 / 8.36), и их принимали за привязанный просчёт (QA stage
              24.09). Выбрать другой расчёт можно в «Истории» выше — он и откроет лист. */}
          {linkedCalculation !== null && loadedResult === null ? (
            linkedFailed ? (
              <Alert
                type="error"
                showIcon
                data-testid="linked-calc-unavailable"
                message={t('idea_calc.messages.linked_calc_unavailable')}
              />
            ) : (
              <Flex justify="center" align="center" gap="small" style={{ padding: token.paddingLG }} data-testid="linked-calc-loading">
                <Spin />
                {t('idea_calc.messages.linked_calc_loading')}
              </Flex>
            )
          ) : (
            <>
              {/* Два вида страницы (WS 15587410, комментарий 25.09): лента и вкладки как в
                  софте Игоря. Дерево компонентов в обоих одно и то же — меняются только
                  видимость и ширина. Перемонтирование при переключении потеряло бы
                  несохранённые правки: калькулятор, бюджет и план берут начальные
                  значения из загруженного расчёта, а не из текущего ввода. */}
              {productMissing && (
                <Alert
                  type="warning"
                  showIcon
                  data-testid="product-data-missing"
                  message={t('idea_calc.calculator.product_missing_banner')}
                  action={
                    <Button size="small" onClick={() => setManualForAsin(productData.asin)}>
                      {t('idea_calc.calculator.product_missing_accept')}
                    </Button>
                  }
                />
              )}
              <ResultBar
                unverified={resultsUnverified}
                data={calculatorData}
                launchBudget={totalLaunchBudget}
                sections={tabs ? TAB_SECTIONS : FEED_SECTIONS}
                mode={viewMode}
                onModeChange={changeViewMode}
                activeTab={activeTab}
                onSelectTab={setActiveTab}
              />
              <Row
                gutter={[16, 16]}
                id="calculator"
                align="stretch"
                style={shown('calculator') || summaryShown ? undefined : { display: 'none' }}
              >
                <Col xs={24} lg={12} style={{ display: shown('calculator') ? 'flex' : 'none' }}>
                  {/* Загруженный расчёт (история, ссылка) показывается своими числами:
                      перемонтированный по loadKey калькулятор иначе снова подставил бы
                      поверх него поля карточки товара. */}
                  <Calculator key={`calc-${loadKey}`} onDataChange={handleCalcChange} onProductDataStatus={handleProductDataStatus} initialData={calcInitial} productCard={loadedResult ? null : productCard} exchangeXid={loadedResult ? null : exchangeXid} onBindParent={handleBindParent} />
                </Col>
                <Col xs={24} lg={summaryAlone ? 24 : 12} style={{ display: summaryShown ? 'flex' : 'none' }}>
                  <CalculatorSummary data={calculatorData} launchBudget={totalLaunchBudget} couponPromoEnabled={calculatorData?.couponPromoEnabled} />
                </Col>
              </Row>
              <div hidden={!shown('score')}>
                <CriteriaScore
                  data={calculatorData}
                  launchBudget={totalLaunchBudget}
                  manual={manualCriteria}
                  onManualChange={setManualCriteria}
                />
              </div>
              <div hidden={!shown('budget')}>
              <FoldSection
                forceOpen={tabs}
                id="budget"
                title={t('idea_calc.budget.title')}
                summary={
                  <Flex align="center" gap="small" wrap>
                    <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>{formatMoney(totalLaunchBudget)}</Typography.Text>
                    {budgetCategories.map((cat) => (
                      <Tag key={cat.id} color={cat.color}>{cat.name}: {formatMoney(cat.total)}</Tag>
                    ))}
                  </Flex>
                }
              >
                <Budget
                  key={`budget-${loadKey}`}
                  cogs={calculatorData ? computeCogs(calculatorData) : undefined}
                  fullUnitCost={calculatorData ? computeFullUnitCost(calculatorData) : undefined}
                  initialData={budgetInitial}
                  onDataChange={setBudgetData}
                />
              </FoldSection>
              </div>
              {/* Конкуренты и BSR — сразу после бюджета, перед планом продаж: график
                  Keepa сверяют с графиком продаж, и стоять они должны рядом. Порядок
                  внутри: плитки → список конкурентов → график на всю ширину. */}
              <div hidden={!shown('competitor-analysis')}>
              <Flex vertical gap={20}>
              {tabs && !asin && competitorAsins.length === 0 && (
                <Alert type="info" showIcon message={t('idea_calc.result_bar.competitors_need_asin')} />
              )}
              {(asin || competitorAsins.length > 0) && (
                <div id="competitor-analysis">
                  <CompetitorAnalysis
                    mainAsin={asin}
                    competitorAsins={competitorAsins}
                    ourPrice={calculatorData?.sellingPrice}
                    ourCogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 0}
                  />
                </div>
              )}
              {/* По одному ASIN, как в старом туле: расчёт из истории или по ссылке несёт ASIN,
                  но не признак «загружено из Keepa» — с ним график у загруженных расчётов
                  не появлялся вовсе. */}
              {asin && <BsrChart asin={asin} />}
              </Flex>
              </div>
              <div id="sales-plan" hidden={!shown('sales-plan')}>
                <SalesPlan
                  key={`sp-${loadKey}`}
                  onDataChange={setSalesPlanData}
                  asin={asin}
                  competitorAsins={competitorAsins}
                  purchasePrice={calculatorData?.purchasePrice}
                  initialData={salesPlanInitial}
                />
              </div>
              <div hidden={!shown('forecast')}>
              <FoldSection
                forceOpen={tabs}
                id="forecast"
                title={t('idea_calc.fold.forecast_title')}
                summary={
                  <Flex align="center" gap="small" wrap>
                    <Typography.Text type="secondary">{t('idea_calc.fold.forecast_sales')}</Typography.Text>
                    {forecastMonths.map((m) => (
                      <Tag key={m.month}>{m.month}: {m.units.toLocaleString()}</Tag>
                    ))}
                  </Flex>
                }
              >
                <ForecastAmazonUS
                  calculatorData={calculatorData}
                  salesPlanData={salesPlanData}
                  launchBudget={totalLaunchBudget}
                  onSummaryChange={setForecastSummary}
                />
              </FoldSection>
              </div>
              <div id="summary" hidden={!shown('summary')}>
                <Summary forecastSummary={forecastSummary} />
              </div>
              <div id="notes" hidden={!shown('notes')}>
                <Notes key={`notes-${loadKey}`} initialNotes={notesInitial} onDataChange={setNotesData} />
              </div>
            </>
          )}
          </div>
        </div>
        </ConfigProvider>
      </Layout.Content>
    </Layout>
  );
};

export default DashboardView;
